import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Gauge, ArrowRight, CheckCircle, Clock } from 'lucide-react';
import { getUsedMachines } from '../services/apiService';
import { useSiteSettings } from '../contexts/SiteSettingsContext';

const Occasions = () => {
  const { settings } = useSiteSettings();
  const [usedMachines, setUsedMachines] = useState({ available: [], sold: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUsedMachines = async () => {
      try {
        setLoading(true);
        const data = await getUsedMachines();
        
        // Group by status
        const grouped = {
          available: data.filter(m => m.status === 'available'),
          sold: data.filter(m => m.status === 'sold')
        };
        
        setUsedMachines(grouped);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching used machines:', error);
        setLoading(false);
      }
    };

    fetchUsedMachines();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 mx-auto mb-4" style={{ borderColor: '#5C3A21' }}></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-gray-900 to-gray-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-amber-400 rounded-full blur-3xl"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Machines Laser d'Occasion
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Démarrez votre activité avec une machine d'occasion révisée et testée par nos soins.<br />
            Machines contrôlées, garanties et formation incluse.
          </p>
        </div>
      </section>

      {/* Available Machines */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: '#5C3A21' }}>
              Machines disponibles
            </h2>
            <p className="text-xl text-gray-600">
              Machines d'occasion révisées, testées et garanties
            </p>
          </div>

          {usedMachines.available.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {usedMachines.available.map((machine) => (
                <div
                  key={machine.id}
                  className="group bg-gradient-to-br from-amber-50 to-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden border-2 border-amber-100"
                >
                  <div className="aspect-[16/10] overflow-hidden relative bg-gray-50 flex items-center justify-center">
                    <div className="absolute top-4 right-4 z-10">
                      <span className="px-4 py-2 rounded-full text-sm font-bold bg-green-500 text-white shadow-lg">
                        Disponible
                      </span>
                    </div>
                    <img
                      src={machine.image}
                      alt={machine.name}
                      className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500 p-2"
                    />
                  </div>
                  <div className="p-8">
                    <div className="flex items-center justify-between mb-4">
                      <span className="px-3 py-1 rounded-full text-sm font-semibold text-white" style={{ backgroundColor: '#5C3A21' }}>
                        {machine.type}
                      </span>
                      <span className="text-sm text-gray-600">{machine.year}</span>
                    </div>

                    <h3 className="text-2xl font-bold mb-3" style={{ color: '#5C3A21' }}>
                      {machine.name}
                    </h3>
                    <p className="text-gray-700 mb-6 leading-relaxed whitespace-pre-line">
                      {machine.description}
                    </p>

                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div className="p-4 rounded-lg bg-white border border-gray-200">
                        <div className="flex items-center space-x-2 mb-1">
                          <Gauge className="w-4 h-4" style={{ color: '#5C3A21' }} />
                          <span className="text-xs font-semibold text-gray-600">Puissance</span>
                        </div>
                        <p className="font-bold text-gray-900">{machine.power}</p>
                      </div>
                      <div className="p-4 rounded-lg bg-white border border-gray-200">
                        <div className="flex items-center space-x-2 mb-1">
                          <Clock className="w-4 h-4" style={{ color: '#5C3A21' }} />
                          <span className="text-xs font-semibold text-gray-600">Utilisation</span>
                        </div>
                        <p className="font-bold text-gray-900">{machine.hours}</p>
                      </div>
                    </div>

                    <div className="mb-6 p-4 rounded-lg" style={{ backgroundColor: '#F5EBDD' }}>
                      <p className="text-sm text-gray-700 mb-1">État : <span className="font-semibold">{machine.condition}</span></p>
                      <p className="text-sm text-gray-700">Zone de travail : <span className="font-semibold">{machine.workArea}</span></p>
                    </div>

                    <div className="mb-6">
                      <h4 className="font-semibold mb-3 text-gray-900">Inclus dans cette offre :</h4>
                      <div className="grid grid-cols-2 gap-2">
                        {machine.included.map((item, idx) => (
                          <div key={idx} className="flex items-start space-x-2">
                            <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#5C3A21' }} />
                            <span className="text-sm text-gray-700">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                      <div>
                        <p className="text-sm text-gray-600 line-through mb-1">{machine.originalPrice}</p>
                        <p className="text-3xl font-bold" style={{ color: '#5C3A21' }}>{machine.price}</p>
                      </div>
                      <Link
                        to="/contact"
                        className="inline-flex items-center px-6 py-3 rounded-lg text-white font-semibold shadow-md hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
                        style={{ backgroundColor: '#5C3A21' }}
                      >
                        Je suis intéressé
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-xl text-gray-600">Aucune machine d'occasion disponible pour le moment.</p>
              <p className="text-gray-500 mt-4">Contactez-nous pour être informé des prochaines arrivées.</p>
            </div>
          )}
        </div>
      </section>

      {/* Sold Machines */}
      {usedMachines.sold.length > 0 && (
        <section className="py-24" style={{ backgroundColor: '#F5EBDD' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: '#5C3A21' }}>
                Machines récemment vendues
              </h2>
              <p className="text-xl text-gray-700">
                Des professionnels nous font confiance
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {usedMachines.sold.map((machine) => (
                <div
                  key={machine.id}
                  className="relative group bg-white rounded-2xl shadow-lg overflow-hidden opacity-75 grayscale hover:grayscale-0 hover:opacity-100 transition-all duration-300"
                >
                  <div className="absolute inset-0 bg-gray-900/50 z-10 flex items-center justify-center">
                    <span className="px-6 py-3 rounded-full text-lg font-bold bg-white/90 backdrop-blur-sm" style={{ color: '#5C3A21' }}>
                      VENDUE
                    </span>
                  </div>
                  <div className="aspect-[4/3] overflow-hidden bg-gray-50 flex items-center justify-center">
                    <img
                      src={machine.image}
                      alt={machine.name}
                      className="w-full h-full object-contain p-2"
                    />
                  </div>
                  <div className="p-6">
                    <span className="px-3 py-1 rounded-full text-xs font-semibold text-white" style={{ backgroundColor: '#5C3A21' }}>
                      {machine.type}
                    </span>
                    <h3 className="text-xl font-bold mt-3 mb-2" style={{ color: '#5C3A21' }}>
                      {machine.name}
                    </h3>
                    <p className="text-sm text-gray-600 mb-2">{machine.power} • {machine.workArea}</p>
                    <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                      <span className="text-sm text-gray-600">
                        <Calendar className="inline w-4 h-4 mr-1" />
                        {machine.soldDate}
                      </span>
                      <span className="font-bold text-gray-900">{machine.soldPrice}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Why Buy Used */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#5C3A21' }}>
                Pourquoi acheter une machine d'occasion chez AKM Laser ?
              </h2>
              <div className="space-y-6">
                {[
                  {
                    title: 'Machines révisées et testées',
                    text: 'Chaque machine est contrôlée, nettoyée, calibrée et testée dans notre atelier avant la vente.'
                  },
                  {
                    title: 'Garantie incluse',
                    text: 'Toutes nos occasions sont garanties 6 mois pièces et main d\'œuvre pour votre tranquillité.'
                  },
                  {
                    title: 'Formation offerte',
                    text: 'Formation à la prise en main de la machine et du logiciel, comme pour une machine neuve.'
                  },
                  {
                    title: 'Prix attractif',
                    text: 'Économisez jusqu\'à 40% par rapport au neuf tout en bénéficiant de la qualité AKM Laser.'
                  },
                  {
                    title: 'Support technique',
                    text: 'Même SAV que pour nos machines neuves : assistance à distance, pièces détachées, conseils.'
                  }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start space-x-4">
                    <CheckCircle className="w-6 h-6 flex-shrink-0 mt-1" style={{ color: '#5C3A21' }} />
                    <div>
                      <h3 className="font-bold text-lg mb-1" style={{ color: '#5C3A21' }}>{item.title}</h3>
                      <p className="text-gray-700 leading-relaxed">{item.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <img
                src={settings.occasionsHeroImage}
                alt="Atelier AKM Laser"
                className="w-full rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-gray-900 to-gray-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-amber-400 rounded-full blur-3xl"></div>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Vous cherchez une machine d'occasion spécifique ?
          </h2>
          <p className="text-xl text-gray-300 mb-10 leading-relaxed">
            Contactez-nous ! Nous recevons régulièrement de nouvelles machines.<br />
            Nous pouvons aussi vous aider à trouver exactement ce qu'il vous faut.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 rounded-xl bg-white font-semibold shadow-2xl hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-300"
              style={{ color: '#5C3A21' }}
            >
              Me tenir informé
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link
              to="/machines"
              className="inline-flex items-center justify-center px-8 py-4 rounded-xl bg-white/10 backdrop-blur-sm text-white font-semibold border-2 border-white/30 hover:bg-white/20 transition-all duration-300"
            >
              Voir les machines neuves
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Occasions;
